import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MessageSquare, Search } from 'lucide-react';
interface Message {
  id: string;
  from: 'me' | 'them';
  text: string;
  timestamp: Date;
}
interface Conversation {
  id: string;
  name: string;
  avatar: string;
  lastMessage: string;
  unread: number;
  messages: Message[];
}
interface MessagingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  role: 'brand' | 'organizer';
}
// Mock conversations
const mockConversations: Conversation[] = [{
  id: '1',
  name: 'Tech Summit 2024',
  avatar: 'TS',
  lastMessage: 'Looking forward to discussing the sponsorship!',
  unread: 2,
  messages: [{
    id: '1',
    from: 'them',
    text: 'Hi! We loved your brand profile.',
    timestamp: new Date(Date.now() - 3600000)
  }, {
    id: '2',
    from: 'them',
    text: 'Looking forward to discussing the sponsorship!',
    timestamp: new Date(Date.now() - 1800000)
  }]
}, {
  id: '2',
  name: 'Green Festival',
  avatar: 'GF',
  lastMessage: 'Thanks for your interest!',
  unread: 0,
  messages: [{
    id: '1',
    from: 'me',
    text: "Hi, we're interested in sponsoring your event.",
    timestamp: new Date(Date.now() - 86400000)
  }, {
    id: '2',
    from: 'them',
    text: 'Thanks for your interest!',
    timestamp: new Date(Date.now() - 82800000)
  }]
}];
export function MessagingPanel({
  isOpen,
  onClose,
  role
}: MessagingPanelProps) {
  const [conversations] = useState<Conversation[]>(mockConversations);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const handleSend = () => {
    if (!newMessage.trim() || !activeConversation) return;
    // In real app, send to backend
    setNewMessage('');
  };
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <AnimatePresence data-id="element-283">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-284">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} onClick={e => e.stopPropagation()} className="w-full max-w-4xl h-[600px] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex" data-id="element-285">
            {/* Conversations List */}
            <div className="w-80 border-r border-slate-800 flex flex-col" data-id="element-286">
              <div className="p-4 border-b border-slate-800" data-id="element-287">
                <div className="flex items-center justify-between mb-4" data-id="element-288">
                  <h2 className="text-lg font-bold text-white" data-id="element-289">Messages</h2>
                  <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors md:hidden" data-id="element-290">
                    <X size={20} data-id="element-291" />
                  </button>
                </div>
                <div className="relative" data-id="element-292">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-293" />
                  <input type="text" placeholder="Search conversations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-slate-600" data-id="element-294" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto" data-id="element-295">
                {conversations.map(conv => <button key={conv.id} onClick={() => setActiveConversation(conv)} className={`w-full p-4 flex items-center gap-3 hover:bg-slate-800/50 transition-colors text-left ${activeConversation?.id === conv.id ? 'bg-slate-800' : ''}`} data-id="element-296">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${role === 'brand' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`} data-id="element-297">
                      {conv.avatar}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-298">
                      <div className="flex items-center justify-between" data-id="element-299">
                        <span className="font-medium text-white truncate" data-id="element-300">
                          {conv.name}
                        </span>
                        {conv.unread > 0 && <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${role === 'brand' ? 'bg-cyan-500 text-slate-900' : 'bg-purple-500 text-white'}`} data-id="element-301">
                            {conv.unread}
                          </span>}
                      </div>
                      <p className="text-sm text-slate-400 truncate" data-id="element-302">
                        {conv.lastMessage}
                      </p>
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col" data-id="element-303">
              {activeConversation ? <>
                  <div className="p-4 border-b border-slate-800 flex items-center justify-between" data-id="element-304">
                    <div className="flex items-center gap-3" data-id="element-305">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${role === 'brand' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`} data-id="element-306">
                        {activeConversation.avatar}
                      </div>
                      <span className="font-medium text-white" data-id="element-307">
                        {activeConversation.name}
                      </span>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-308">
                      <X size={20} data-id="element-309" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-310">
                    {activeConversation.messages.map(msg => <div key={msg.id} className={`flex ${msg.from === 'me' ? 'justify-end' : 'justify-start'}`} data-id="element-311">
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${msg.from === 'me' ? role === 'brand' ? 'bg-cyan-500 text-slate-900' : 'bg-purple-600 text-white' : 'bg-slate-800 text-white'}`} data-id="element-312">
                          <p data-id="element-313">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.from === 'me' ? 'opacity-70' : 'text-slate-500'}`} data-id="element-314">
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>)}
                  </div>

                  <div className="p-4 border-t border-slate-800" data-id="element-315">
                    <div className="flex gap-3" data-id="element-316">
                      <input type="text" placeholder="Type a message..." value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-slate-600" data-id="element-317" />
                      <button onClick={handleSend} disabled={!newMessage.trim()} className={`p-3 rounded-full transition-all disabled:opacity-50 ${buttonColor}`} data-id="element-318">
                        <Send size={20} data-id="element-319" />
                      </button>
                    </div>
                  </div>
                </> : <div className="flex-1 flex items-center justify-center text-center p-8" data-id="element-320">
                  <div data-id="element-321">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`} data-id="element-322">
                      <MessageSquare size={32} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-323" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-324">
                      Select a conversation
                    </h3>
                    <p className="text-slate-400" data-id="element-325">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}